const PRODUCT_TEMPLATE_SELECTOR = 'main[data-template^="product"]';
const FILTERED_BLOCK_SELECTOR =
  '.accelerated-checkout-block[data-ab-product-page-accelerated-checkout="true"][data-ab-hide-unbranded-dynamic-checkout="true"]';
const ACCELERATED_CHECKOUT_SELECTOR = 'shopify-accelerated-checkout';
const LEGACY_UNBRANDED_SELECTOR = 'button.shopify-payment-button__button--unbranded';

export const BRANDED_ACCELERATED_CHECKOUT_NAMES = new Set([
  'paypal',
  'shop_pay',
  'apple_pay',
  'google_pay',
  'amazon_pay',
  'venmo',
]);

export function normalizePaymentName(name) {
  return String(name || '')
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, '_');
}

export function recommendedPaymentName(host) {
  const raw = host?.getAttribute?.('recommended');
  if (!raw) return '';

  try {
    const payload = JSON.parse(raw);
    return normalizePaymentName(payload?.name);
  } catch (_error) {
    return '';
  }
}

export function isBrandedAcceleratedCheckout(host) {
  return BRANDED_ACCELERATED_CHECKOUT_NAMES.has(recommendedPaymentName(host));
}

export function isProductPage(root = document) {
  return Boolean(root?.querySelector?.(PRODUCT_TEMPLATE_SELECTOR));
}

function suppressBlock(block) {
  block.dataset.abSuppressedUnbranded = 'true';
  block.setAttribute('aria-hidden', 'true');
}

function unsuppressBlock(block) {
  if (block.dataset.abSuppressedUnbranded !== 'true') return;

  delete block.dataset.abSuppressedUnbranded;
  block.removeAttribute('aria-hidden');
}

function hideLegacyUnbrandedButton(button) {
  button.hidden = true;
  button.setAttribute('aria-hidden', 'true');
  button.dataset.abSuppressedUnbranded = 'true';
}

export function syncAcceleratedCheckoutBlock(block) {
  if (!(block instanceof HTMLElement)) return false;

  const host = block.querySelector(ACCELERATED_CHECKOUT_SELECTOR);
  if (host) {
    if (isBrandedAcceleratedCheckout(host)) {
      unsuppressBlock(block);
      return true;
    }

    suppressBlock(block);
    return false;
  }

  const legacyUnbrandedButton = block.querySelector(LEGACY_UNBRANDED_SELECTOR);
  if (legacyUnbrandedButton instanceof HTMLElement) {
    hideLegacyUnbrandedButton(legacyUnbrandedButton);
    return false;
  }

  unsuppressBlock(block);
  return true;
}

export function syncAcceleratedCheckoutBlocks(root = document) {
  if (!isProductPage(root)) return [];

  return Array.from(root.querySelectorAll(FILTERED_BLOCK_SELECTOR)).map(syncAcceleratedCheckoutBlock);
}

export function initAcceleratedCheckoutFilter(root = document) {
  if (!isProductPage(root)) return null;

  syncAcceleratedCheckoutBlocks(root);

  const observer = new MutationObserver(() => {
    syncAcceleratedCheckoutBlocks(root);
  });

  observer.observe(root.documentElement || root, {
    attributes: true,
    attributeFilter: ['recommended', 'hidden', 'style'],
    childList: true,
    subtree: true,
  });

  return observer;
}

if (typeof document !== 'undefined') {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAcceleratedCheckoutFilter(), { once: true });
  } else {
    initAcceleratedCheckoutFilter();
  }
}
